/**
 * A ligação em tempo real do chat: UMA por separador, não uma por conversa (ADR-0017).
 *
 * - Abre `wss://…/api/v1/chat/ws` (a sessão vai no cookie); volta a ligar com backoff e jitter.
 * - Ao abrir, pede a retoma por cursor (`resume`): o servidor repõe da base tudo o que entrou entretanto.
 * - Batimento a cada `heartbeat_seconds`; sem resposta, a ligação é dada como morta e reaberta.
 * - Ao fim de várias falhas seguidas passa a modo de consulta (`onFallback`): a app pergunta por cursor
 *   (`GET /chat/events`) enquanto continua a tentar o WebSocket.
 *
 * Sem React: os hooks ligam isto à cache do TanStack Query. Nenhum temporizador sobrevive a `stop()`.
 */

import { API_BASE_URL } from "@/lib/api";
import { delayAfterClose, FALLBACK_AFTER_ATTEMPTS } from "@/lib/chat/backoff";
import { eventValidatorReady, loadEventValidator, parseRealtimeEvent, type ChatRealtimeEvent } from "@/lib/chat/realtime-events";

export type ConnectionStatus = "a_ligar" | "ligado" | "sem_ligacao";

export interface RealtimeHandlers {
  onEvent: (event: ChatRealtimeEvent) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Cursores de retoma: o último `seq` visto por conversa, e desde quando a ligação esteve em baixo. */
  resumeState: () => { cursors: Record<string, number>; since: string | null };
  /** `true`: o WebSocket não está a dar; a app consulta por cursor até ele voltar. */
  onFallback: (active: boolean) => void;
}

export function websocketUrl(base: string = API_BASE_URL, location: Pick<Location, "protocol" | "host"> = window.location): string {
  const absolute = /^https?:\/\//.test(base) ? base : `${location.protocol}//${location.host}${base}`;
  return `${absolute.replace(/^http/, "ws").replace(/\/$/, "")}/chat/ws`;
}

export class RealtimeConnection {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  private downSince: string | null = null;
  private watched = new Set<string>();
  // Quando chegou a última coisa do servidor. Sem nada durante 3 batimentos (nem a resposta ao `hb`), a ligação está
  // semiaberta (Wi-Fi que mudou, proxy): fecha-se e volta a ligar.
  private lastReceivedAt = 0;

  constructor(
    private readonly handlers: RealtimeHandlers,
    private readonly createSocket: (url: string) => WebSocket = (url) => new WebSocket(url),
    private readonly url: string = websocketUrl(),
  ) {}

  start(): void {
    this.stopped = false;
    // Com o validador já carregado abre-se logo; senão carrega-se primeiro (um pedaço à parte, ver `realtime-events`).
    if (eventValidatorReady()) this.open();
    else void loadEventValidator().then(() => this.open());
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState <= WebSocket.OPEN) socket.close(1000);
  }

  /** Um canal público que a pessoa está a ver sem ser membro: subscreve-o só enquanto está aberto. */
  watch(conversationId: string): void {
    this.watched.add(conversationId);
    this.send({ type: "subscribe", conversation_id: conversationId });
  }

  unwatch(conversationId: string): void {
    this.watched.delete(conversationId);
    this.send({ type: "unsubscribe", conversation_id: conversationId });
  }

  typing(conversationId: string, threadRootId: string | null): void {
    this.send({ type: "typing", conversation_id: conversationId, thread_root_id: threadRootId });
  }

  presence(status: "online" | "ausente"): void {
    this.send({ type: "presence", status });
  }

  private send(frame: Record<string, unknown>): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(frame));
  }

  private open(): void {
    if (this.stopped) return;
    this.handlers.onStatus("a_ligar");
    let socket: WebSocket;
    try {
      socket = this.createSocket(this.url);
    } catch {
      this.scheduleReconnect(1006);
      return;
    }
    this.socket = socket;
    socket.onmessage = (message) => {
      this.lastReceivedAt = Date.now();
      if (typeof message.data !== "string") return;
      const parsed = parseRealtimeEvent(message.data);
      if (!parsed.ok) {
        // Recusado e registado: a retoma por cursor repõe o que faltar.
        console.warn("chat: evento em tempo real recusado", parsed.reason);
        return;
      }
      if (parsed.event.type === "ready") this.onReady(parsed.event.heartbeat_seconds);
      this.handlers.onEvent(parsed.event);
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearTimers();
      if (!this.stopped) this.scheduleReconnect(event.code);
    };
    socket.onerror = () => {
      // O `onclose` que se segue trata da reconexão.
    };
  }

  private onReady(heartbeatSeconds: number): void {
    const wasDown = this.attempt >= FALLBACK_AFTER_ATTEMPTS;
    this.attempt = 0;
    this.handlers.onStatus("ligado");
    if (wasDown) this.handlers.onFallback(false);
    const { cursors, since } = this.handlers.resumeState();
    this.send({ type: "resume", cursors, since: since ?? this.downSince });
    this.downSince = null;
    for (const id of this.watched) this.send({ type: "subscribe", conversation_id: id });
    const everyMs = Math.max(5, heartbeatSeconds) * 1000;
    this.lastReceivedAt = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (Date.now() - this.lastReceivedAt > everyMs * 3) {
        this.socket?.close(4000, "sem resposta ao batimento");
        return;
      }
      this.send({ type: "hb" });
    }, everyMs);
  }

  private scheduleReconnect(code: number): void {
    this.downSince ??= new Date().toISOString();
    this.handlers.onStatus(this.attempt === 0 && code === 1012 ? "a_ligar" : "sem_ligacao");
    if (this.attempt + 1 === FALLBACK_AFTER_ATTEMPTS) this.handlers.onFallback(true);
    const delay = delayAfterClose(code, this.attempt);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
  }
}
